/**
 * Détection de l'écriture (script Unicode) d'un livre, partagée serveur et client.
 *
 * Pourquoi : les deux moteurs de rendu du livre ne savent pas se débrouiller seuls avec
 * les caractères non latins.
 *
 * - Le contenu et l'aperçu sont du HTML : le moteur choisit la police glyphe par glyphe
 *   dans la pile `font-family`, il suffit donc d'ajouter la bonne Noto à la pile via une
 *   classe `lang-*` posée sur le body.
 * - La couverture imprimeur est dessinée par PdfKit, qui embarque une seule police par
 *   bloc de texte et n'a aucun repli. Il faut choisir une police unique qui couvre à la
 *   fois l'écriture détectée et le latin, sinon un titre mixte comme « Marie 你好 »
 *   ressort en carrés d'un côté ou de l'autre.
 *
 * Un livre n'a donc qu'une écriture « principale ». En cas de mélange, l'ordre de
 * SCRIPT_PRIORITY tranche : on privilégie l'écriture que les polices latines par défaut
 * (Poppins, Roboto) ne savent pas rendre du tout.
 */

// Écritures dont les glyphes manquent totalement à Poppins et Roboto : sans police
// dédiée, le texte sort en carrés.
const HANGUL_REGEXP = /[\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uAC00-\uD7AF]/;
const KANA_REGEXP = /[\u3040-\u30FF\u31F0-\u31FF]/;
const HAN_REGEXP = /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/;
const ARABIC_REGEXP = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const HEBREW_REGEXP = /[\u0590-\u05FF\uFB1D-\uFB4F]/;
const GEORGIAN_REGEXP = /[\u10A0-\u10FF\u1C90-\u1CBF\u2D00-\u2D2F]/;
const ARMENIAN_REGEXP = /[\u0530-\u058F\uFB13-\uFB17]/;
const THAI_REGEXP = /[\u0E00-\u0E7F]/;
// Lettres de base seulement : les signes combinants (virama, nukta) suivent toujours
// une consonne, les inclure ne changerait rien à la détection.
const DEVANAGARI_REGEXP = /[\u0904-\u0939\u0958-\u0961\u0972-\u097F]/;

// Écritures que Roboto couvre mais pas Poppins : seuls les titres de couverture,
// dessinés en Poppins, posent problème.
const CYRILLIC_REGEXP = /[\u0400-\u0482\u048A-\u052F\uA640-\uA66E\uA680-\uA69B]/;
const GREEK_REGEXP = /[\u0370-\u03FF\u1F00-\u1FFF]/;
// Latin étendu additionnel (vietnamien surtout) : Poppins n'a pas ệ, ữ, ờ, ậ…
const LATIN_EXTENDED_REGEXP = /[\u1EA0-\u1EFF]/;

/**
 * Ordre de priorité quand plusieurs écritures cohabitent dans un même livre.
 * Les écritures totalement absentes des polices latines passent devant, car ce sont
 * elles qui produiraient des carrés. Le japonais et le coréen restent en tête pour
 * préserver le comportement historique.
 */
export const SCRIPT_PRIORITY = ['ko', 'ja', 'arab', 'hebr', 'geor', 'hy', 'thai', 'deva', 'cyrl', 'grek', 'latnExt'];

// Écritures qui se lisent de droite à gauche.
const RTL_SCRIPTS = ['arab', 'hebr'];

// Nombre de pages inspectées : un échantillon suffit et évite de parcourir
// 200 000 messages pour choisir une police.
const SAMPLED_PAGES = 5;

// Première lettre d'un texte, toutes écritures confondues. Chiffres, emojis, signes
// diacritiques et ponctuation n'en sont pas : ils ne décident pas du sens de lecture.
const LETTER_REGEXP = /\p{L}/u;

// Part minimale de bulles en arabe ou en hébreu pour qu'un tome s'ouvre par la droite.
// Un livre français qui cite quelques messages arabes reste un livre français.
export const RTL_BOOK_MIN_RATIO = 0.6;

// Le japonais et le coréen sont comptés à part pour départager un livre qui mélange
// kana et hangul.
const SCRIPT_TESTS = [
    ['hangul', HANGUL_REGEXP],
    ['kana', KANA_REGEXP],
    ['han', HAN_REGEXP],
    ['arab', ARABIC_REGEXP],
    ['hebr', HEBREW_REGEXP],
    ['geor', GEORGIAN_REGEXP],
    ['hy', ARMENIAN_REGEXP],
    ['thai', THAI_REGEXP],
    ['deva', DEVANAGARI_REGEXP],
    ['cyrl', CYRILLIC_REGEXP],
    ['grek', GREEK_REGEXP],
    ['latnExt', LATIN_EXTENDED_REGEXP],
];

function scriptsInText(text) {
    if(!text) {
        return [];
    }
    return SCRIPT_TESTS.filter(([, regexp]) => regexp.test(text)).map(([key]) => key);
}

function emptyCounts() {
    return { hangul: 0, kana: 0, han: 0, arab: 0, hebr: 0, geor: 0, hy: 0, thai: 0, deva: 0, cyrl: 0, grek: 0, latnExt: 0 };
}

function resolveScript(counts) {
    // Le CJK d'abord : hangul et kana peuvent coexister, on prend le plus présent.
    if(counts.hangul > 0 || counts.kana > 0) {
        return counts.hangul >= counts.kana ? 'ko' : 'ja';
    }
    // Han seul (chinois, ou japonais sans kana dans l'échantillon) : Noto JP le couvre.
    if(counts.han > 0) {
        return 'ja';
    }
    return SCRIPT_PRIORITY.find(script => counts[script] > 0) || '';
}

/**
 * Écriture principale d'un livre.
 *
 * `language` gagne pour le japonais et le coréen, qui sont des langues d'interface du
 * site. Les autres écritures ne le sont pas (personne ne navigue en arménien ou en
 * géorgien sur MonLivreSMS) : elles sont toujours détectées sur le texte, titres de
 * couverture compris via `extraSample`.
 */
export function detectBookScript(language, book, extraSample = '') {
    if(language === 'ja' || language === 'ko') {
        return language;
    }

    const counts = emptyCounts();
    const count = text => scriptsInText(text).forEach(key => { counts[key] += 1; });

    count(`${extraSample || ''}`);

    const pages = (book && book.pages) || [];
    for(let p = 0; p < pages.length && p < SAMPLED_PAGES; p += 1) {
        const messages = pages[p].messages || [];
        for(let m = 0; m < messages.length; m += 1) {
            count(`${messages[m].body || messages[m].text || ''}`);
        }
    }

    return resolveScript(counts);
}

/**
 * Écriture principale d'un texte isolé (un titre de couverture par exemple).
 */
export function detectTextScript(text) {
    return detectBookScript(null, null, text);
}

/**
 * Classe posée sur le body du livre HTML : book.css y accroche la pile de polices.
 * Le japonais, le coréen et l'arménien gardent leurs classes historiques.
 */
export function getScriptLangClass(script) {
    return script ? `lang-${script}` : '';
}

export function isRtlScript(script) {
    return RTL_SCRIPTS.includes(script);
}

/**
 * Direction d'écriture à appliquer au texte saisi par le client (titres, messages).
 */
export function getScriptDirection(script) {
    return isRtlScript(script) ? 'rtl' : 'ltr';
}

/**
 * Sens de lecture d'un message, fixé par sa première lettre comme sur WhatsApp ou
 * iMessage : 'rtl', 'ltr', ou '' si le texte n'a aucune lettre (emojis, chiffres).
 */
export function getTextDirection(text) {
    const match = `${text || ''}`.match(LETTER_REGEXP);
    if(!match) {
        return '';
    }
    return (ARABIC_REGEXP.test(match[0]) || HEBREW_REGEXP.test(match[0])) ? 'rtl' : 'ltr';
}

/**
 * Sens majoritaire d'une suite de sens de messages ('rtl', 'ltr' ou '').
 * Les messages sans lettre ne votent pas.
 */
export function getDominantDirection(directions = []) {
    let rtl = 0;
    let written = 0;
    directions.forEach(direction => {
        if(direction) {
            written += 1;
            rtl += direction === 'rtl' ? 1 : 0;
        }
    });
    return rtl && rtl >= written * RTL_BOOK_MIN_RATIO ? 'rtl' : 'ltr';
}

/**
 * Sens d'un tome : il s'ouvre par la droite si ses bulles sont majoritairement en
 * arabe ou en hébreu. Toujours décidé sur le tome entier, côté client comme serveur,
 * pour que l'aperçu et le PDF tranchent de la même façon.
 */
export function getBookDirection(pages = []) {
    const directions = [];
    (pages || []).forEach(page => (page.messages || []).forEach(message => {
        // Séparateur Best Of : texte généré dans la langue du site, pas un message.
        if(message.isBestofSeparator) {
            return;
        }
        directions.push(message.textDirection !== undefined ? message.textDirection : getTextDirection(message.text));
    }));
    return getDominantDirection(directions);
}
